import { Box, Button, SpaceBetween } from '@amzn/awsui-components-console';
import { LinkDevicesButton } from '@features/isc/LinkDevicesButton';
import { UnlinkDevicesButton } from '@features/isc/UnlinkDevicesButton';
import {
  Resource,
  ResourceAction,
  useHasResourcePermission,
} from '@hooks/useHasResourcePermission';
import React from 'react';
import {
  addTextToSelected,
  removeTextFromSelected,
} from 'src/features/isc/actions/thunks';
import { ExportCsvButton } from 'src/features/isc/ExportCsvButton';
import { useAppDispatch, useAppSelector } from 'src/stores/slices/hooks';
import { toggleShowFileUpload } from 'src/stores/slices/userSlice';

const ucRegx = /uc_.*/i;
const dcmsnRegx = /dcmsn_.*/i;

export function DeviceMenu(): React.ReactElement {
  const dispatch = useAppDispatch();
  const parentDevices = useAppSelector(
    state => state.deviceState.parentDevices
  );
  const childDevices = useAppSelector(state => state.deviceState.childDevices);
  const subchildDevices = useAppSelector(
    state => state.deviceState.subchildDevices
  );

  function addUcDcmsnDisabled(): boolean {
    return ![childDevices, parentDevices, subchildDevices]
      .flat()
      .some(device => device.checked);
  }

  function removeUcDcmsnDisabled(regxMatch: RegExp): boolean {
    return ![childDevices, parentDevices, subchildDevices]
      .flat()
      .some(device => device.checked && device.device_name.match(regxMatch));
  }
  const { hasPermission: hasDeviceWritePermission } = useHasResourcePermission(
    Resource.Device,
    ResourceAction.Write
  );

  return (
    <Box textAlign="center">
      <SpaceBetween size="s" direction="vertical">
        <Button
          className="ninetyWidth"
          disabled={addUcDcmsnDisabled() || !hasDeviceWritePermission}
          disabledReason={
            !hasDeviceWritePermission
              ? 'Missing Device Write permissions'
              : 'No devices selected'
          }
          onClick={(): void => {
            dispatch(addTextToSelected({ toAdd: 'uc_' }));
          }}
          iconName="add-plus">
          Add uc_
        </Button>
        <Button
          className="ninetyWidth"
          disabled={removeUcDcmsnDisabled(ucRegx) || !hasDeviceWritePermission}
          disabledReason={
            !hasDeviceWritePermission
              ? 'Missing Device Write permissions'
              : 'None devices selected, or selection does not include uc_ names'
          }
          onClick={(): void => {
            dispatch(removeTextFromSelected({ toRemove: 'uc_' }));
          }}
          iconName="close">
          Remove uc_
        </Button>
        <Button
          className="ninetyWidth"
          disabled={addUcDcmsnDisabled() || !hasDeviceWritePermission}
          disabledReason={
            !hasDeviceWritePermission
              ? 'Missing Device Write permissions'
              : 'No devices selected'
          }
          onClick={(): void => {
            dispatch(addTextToSelected({ toAdd: 'dcmsn_' }));
          }}
          iconName="add-plus">
          Add dcmsn_
        </Button>
        <Button
          className="ninetyWidth"
          disabled={
            removeUcDcmsnDisabled(dcmsnRegx) || !hasDeviceWritePermission
          }
          disabledReason={
            !hasDeviceWritePermission
              ? 'Missing Device Write permissions'
              : 'None devices selected, or selection does not include dcmsn_ names'
          }
          onClick={(): void => {
            dispatch(removeTextFromSelected({ toRemove: 'dcmsn_' }));
          }}
          iconName="close">
          Remove dcmsn_
        </Button>
        <Button
          className="ninetyWidth"
          disabled={!hasDeviceWritePermission}
          disabledReason="Missing Device Write permissions"
          onClick={(): void => {
            dispatch(toggleShowFileUpload());
          }}
          iconName="upload">
          Upload CSV
        </Button>
        <ExportCsvButton />
        <LinkDevicesButton />
        <UnlinkDevicesButton />
      </SpaceBetween>
    </Box>
  );
}
